import { useEffect, useId, useState } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, useConfirmDialog } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { useToastStore } from '../stores/toast';

// ============================================
// 타입 정의
// ============================================

interface Industry {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentId?: string | null;
  level: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count: { children: number; hazardMappings: number };
  children?: Industry[];
}

interface IndustryDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentId: string | null;
  level: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  parent: { id: string; code: string; name: string } | null;
  children: Array<{ id: string; code: string; name: string; isActive: boolean }>;
  hazardMappings: Array<{
    id: string;
    hazardTypeId: string;
    hazardType: { id: string; code: string; name: string };
    priority: number;
    percentage: number | null;
    source: string | null;
  }>;
}

// KSIC 최하위(세세분류) 레벨 — 서버 DTO의 `@Max(5)`와 같은 값. 이 레벨은 상위 업종 후보가 아니다.
const MAX_INDUSTRY_LEVEL = 5;
// 목록 보기 한 페이지 행 수(KSIC 11차 2,038행을 한 번에 그리지 않는다).
const LIST_PAGE_SIZE = 50;
// 「소분류까지 펼치기」가 여는 깊이 — L1·L2를 펼치면 소분류(L3)까지 보인다.
const EXPAND_UNTIL_LEVEL = 2;

// ============================================
// 메인 컴포넌트
// ============================================

export default function Industries() {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Industry | null>(null);
  const [detailTarget, setDetailTarget] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  // 목록 보기 필터 — 검색은 서버 `search`(코드·이름 부분일치)로, 입력은 잠깐 멈춘 뒤 보낸다.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 필터가 바뀌면 첫 페이지로.
  useEffect(() => {
    setPage(0);
  }, [search, levelFilter]);

  const formOpen = showAddModal || !!editTarget;

  // 통계 조회
  const { data: statsData } = useQuery({
    queryKey: ['admin', 'industries', 'stats'],
    queryFn: () => adminApi.getIndustryStats(),
  });

  // 트리 조회 — 추가·수정 모달의 상위 업종 후보도 이 전체 트리에서 만든다(목록은 필터로 잘려 있을 수 있다).
  const { data: treeData, isLoading: treeLoading } = useQuery({
    queryKey: ['admin', 'industries', 'tree'],
    queryFn: () => adminApi.getIndustriesTree(),
    enabled: viewMode === 'tree' || formOpen,
  });

  // 리스트 조회
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['admin', 'industries', 'list', { search, level: levelFilter }],
    queryFn: () => adminApi.getIndustries({ search: search || undefined, level: levelFilter }),
    enabled: viewMode === 'list',
    placeholderData: keepPreviousData,
  });

  const listRows = listData?.data || [];
  const pageCount = Math.max(1, Math.ceil(listRows.length / LIST_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = listRows.slice(
    currentPage * LIST_PAGE_SIZE,
    (currentPage + 1) * LIST_PAGE_SIZE,
  );

  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.toggleIndustryActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'industries'] });
    },
    // 전역 mutation 기본 onError를 두지 않는다(onError를 가진 화면이 이중 토스트가 된다) —
    // 거부 사유는 화면마다 여기서 띄운다.
    onError: (err) => showToast(apiErrorMessage(err, '활성 상태 변경에 실패했습니다.'), 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteIndustry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'industries'] });
    },
    // 확인 문구가 예고하는 「하위 업종이 있으면 삭제할 수 없습니다」 거부가 여기 닿는다.
    onError: (err) => showToast(apiErrorMessage(err, '업종 삭제에 실패했습니다.'), 'error'),
  });

  // 트리·목록 두 보기가 같은 확인을 쓴다(종전엔 같은 confirm 문구를 두 자리에 손으로 적었다).
  const handleDelete = async (id: string) => {
    if (
      await confirm({
        title: '업종 삭제',
        body: '이 업종을 삭제하시겠습니까? 하위 업종이 있으면 삭제할 수 없습니다.',
        confirmLabel: '삭제',
        danger: true,
      })
    ) {
      deleteMutation.mutate(id);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // 전부 펼치면 2,038행을 한 번에 그리므로 소분류까지만 연다 — 그 아래는 행마다 펼친다.
  const expandToSubclass = () => {
    if (treeData?.data) {
      const ids = new Set<string>();
      const walk = (items: Industry[]) => {
        items.forEach((item) => {
          if (item.level > EXPAND_UNTIL_LEVEL) return;
          ids.add(item.id);
          if (item.children) walk(item.children);
        });
      };
      walk(treeData.data);
      setExpandedItems(ids);
    }
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  const isLoading = viewMode === 'tree' ? treeLoading : listLoading;
  const stats = statsData?.data;

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">업종 관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          한국표준산업분류(KSIC) 기반 업종을 관리합니다.
        </p>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card radius="lg">
            <div className="text-sm text-gray-500">전체</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">활성</div>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">비활성</div>
            <div className="text-2xl font-bold text-gray-400">{stats.inactive}</div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">레벨별</div>
            <div className="text-sm text-gray-700">
              {stats.byLevel.map((l) => `L${l.level}: ${l.count}`).join(', ')}
            </div>
          </Card>
        </div>
      )}

      {/* 도구 바 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {/* 보기 모드 전환 */}
          <div className="flex bg-gray-100 rounded p-1">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'tree'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              트리
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'list'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              목록
            </button>
          </div>

          {viewMode === 'tree' && (
            <>
              <button
                onClick={expandToSubclass}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                소분류까지 펼치기
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                전체 접기
              </button>
            </>
          )}

          {viewMode === 'list' && (
            <>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="코드·업종명 검색"
                aria-label="업종 검색"
                className="w-56 px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <select
                value={levelFilter ?? ''}
                onChange={(e) =>
                  setLevelFilter(e.target.value ? Number(e.target.value) : undefined)
                }
                aria-label="레벨 필터"
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">전체 레벨</option>
                {Array.from({ length: MAX_INDUSTRY_LEVEL }, (_, i) => i + 1).map((lv) => (
                  <option key={lv} value={lv}>
                    L{lv}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <span>+</span>
          <span>업종 추가</span>
        </Button>
      </div>

      {/* 콘텐츠 */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      ) : viewMode === 'tree' ? (
        <TreeView
          data={treeData?.data || []}
          expandedItems={expandedItems}
          onToggleExpand={toggleExpand}
          onEdit={setEditTarget}
          onDelete={handleDelete}
          onToggleActive={(id) => toggleActiveMutation.mutate(id)}
          onViewDetail={setDetailTarget}
        />
      ) : (
        <>
          <ListView
            data={pageRows}
            emptyMessage={
              search || levelFilter !== undefined
                ? '조건에 맞는 업종이 없습니다.'
                : '등록된 업종이 없습니다.'
            }
            onEdit={setEditTarget}
            onDelete={handleDelete}
            onToggleActive={(id) => toggleActiveMutation.mutate(id)}
            onViewDetail={setDetailTarget}
          />
          {listRows.length > 0 && (
            <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
              <span>
                {listRows.length}개 중 {currentPage * LIST_PAGE_SIZE + 1}–
                {currentPage * LIST_PAGE_SIZE + pageRows.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                <span>
                  {currentPage + 1} / {pageCount}
                </span>
                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage >= pageCount - 1}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* 추가/수정 모달 */}
      {formOpen && (
        <IndustryModal
          industry={editTarget}
          parentOptions={treeData?.data || []}
          parentOptionsLoading={treeLoading}
          onClose={() => {
            setShowAddModal(false);
            setEditTarget(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'industries'] });
            setShowAddModal(false);
            setEditTarget(null);
          }}
        />
      )}

      {/* 상세 모달 */}
      {detailTarget && (
        <IndustryDetailModal
          industryId={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      )}
      {confirmDialog}
    </div>
  );
}

// ============================================
// 트리 뷰
// ============================================

function TreeView({
  data,
  expandedItems,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleActive,
  onViewDetail,
}: {
  data: Industry[];
  expandedItems: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (industry: Industry) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onViewDetail: (id: string) => void;
}) {
  return (
    <Card padding="none" radius="lg">
      {data.length === 0 ? (
        <div className="p-8 text-center text-gray-500">등록된 업종이 없습니다.</div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {data.map((item) => (
            <TreeItem
              key={item.id}
              item={item}
              depth={0}
              expandedItems={expandedItems}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
              onViewDetail={onViewDetail}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function TreeItem({
  item,
  depth,
  expandedItems,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleActive,
  onViewDetail,
}: {
  item: Industry;
  depth: number;
  expandedItems: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (industry: Industry) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onViewDetail: (id: string) => void;
}) {
  const isExpanded = expandedItems.has(item.id);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <li>
      <div
        className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 ${
          !item.isActive ? 'opacity-50' : ''
        }`}
        style={{ paddingLeft: `${depth * 24 + 16}px` }}
      >
        {/* 확장 버튼 */}
        <button
          onClick={() => onToggleExpand(item.id)}
          className={`w-5 h-5 flex items-center justify-center text-gray-400 ${
            !hasChildren ? 'invisible' : ''
          }`}
        >
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* 클릭 가능한 영역 (코드 + 이름) */}
        <button
          onClick={() => onViewDetail(item.id)}
          className="flex items-center gap-3 flex-1 text-left hover:text-brand-600 transition-colors"
        >
          <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded text-gray-600">
            {item.code}
          </span>
          <span className="font-medium text-gray-900">{item.name}</span>
        </button>

        {/* 매핑 수 */}
        {(item._count?.hazardMappings ?? 0) > 0 && (
          <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded">
            위험유형 {item._count?.hazardMappings}개
          </span>
        )}

        {/* 상태 */}
        <button
          onClick={() => onToggleActive(item.id)}
          className={`px-2 py-0.5 rounded text-xs ${
            item.isActive
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {item.isActive ? '활성' : '비활성'}
        </button>

        {/* 액션 */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(item)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="수정"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1 text-gray-400 hover:text-red-600"
            title="삭제"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* 하위 항목 */}
      {isExpanded && hasChildren && (
        <ul>
          {item.children!.map((child) => (
            <TreeItem
              key={child.id}
              item={child}
              depth={depth + 1}
              expandedItems={expandedItems}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
              onViewDetail={onViewDetail}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ============================================
// 리스트 뷰
// ============================================

function ListView({
  data,
  emptyMessage,
  onEdit,
  onDelete,
  onToggleActive,
  onViewDetail,
}: {
  data: Industry[];
  emptyMessage: string;
  onEdit: (industry: Industry) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onViewDetail: (id: string) => void;
}) {
  return (
    <Card padding="none" radius="lg" className="overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              코드
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              업종명
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              레벨
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              하위/매핑
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              상태
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              관리
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr key={item.id} className={!item.isActive ? 'opacity-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onViewDetail(item.id)}
                    className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded hover:bg-brand-100 hover:text-brand-700 transition-colors"
                  >
                    {item.code}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onViewDetail(item.id)}
                    className="text-left hover:text-brand-600 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{item.name}</span>
                    {item.description && (
                      <p className="text-sm text-gray-500 truncate max-w-xs">{item.description}</p>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  L{item.level}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item._count?.children ?? 0}개 / {item._count?.hazardMappings ?? 0}개
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onToggleActive(item.id)}
                    className={`px-2 py-1 rounded text-xs ${
                      item.isActive
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {item.isActive ? '활성' : '비활성'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  <button
                    onClick={() => onEdit(item)}
                    className="text-gray-600 hover:text-gray-800 mr-3"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Card>
  );
}

// ============================================
// 업종 추가/수정 모달
// ============================================

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function IndustryModal({
  industry,
  parentOptions,
  parentOptionsLoading = false,
  onClose,
  onSuccess,
}: {
  industry: Industry | null;
  /** 전체 업종 트리(필터로 잘린 목록을 넘기지 말 것 — 현재 상위 업종이 후보에서 빠진다). */
  parentOptions: Industry[];
  /** 트리를 불러오는 동안 select를 잠근다 — 후보가 비어 있으면 수정 중인 상위 업종이 「없음」으로 보인다. */
  parentOptionsLoading?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [code, setCode] = useState(industry?.code || '');
  const [name, setName] = useState(industry?.name || '');
  const [description, setDescription] = useState(industry?.description || '');
  const [parentId, setParentId] = useState(industry?.parentId || '');
  const [isActive, setIsActive] = useState(industry?.isActive ?? true);
  const [error, setError] = useState('');
  const formId = useId();

  const isEdit = !!industry;

  // 부모 선택 옵션 평탄화
  const flattenOptions = (items: Industry[], depth = 0): Array<{ id: string; label: string }> => {
    const result: Array<{ id: string; label: string }> = [];
    items.forEach((item) => {
      // 수정 시 자기 자신과 하위 항목은 선택 불가
      if (isEdit && (item.id === industry.id)) return;
      // 세세분류(최하위)는 하위를 가질 수 없다 — 그 아래 행이 없으므로 재귀도 여기서 끝난다.
      if (item.level >= MAX_INDUSTRY_LEVEL) return;
      result.push({
        id: item.id,
        label: `${'　'.repeat(depth)}${item.code} - ${item.name}`,
      });
      if (item.children) {
        result.push(...flattenOptions(item.children, depth + 1));
      }
    });
    return result;
  };

  const parentSelectOptions = flattenOptions(parentOptions);

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return await adminApi.updateIndustry(industry.id, {
          code,
          name,
          description: description || undefined,
          parentId: parentId || null,
          isActive,
        });
      } else {
        return await adminApi.createIndustry({
          code,
          name,
          description: description || undefined,
          parentId: parentId || undefined,
          isActive,
        });
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error?.message || '저장에 실패했습니다.');
      }
    },
    onError: () => {
      setError('저장 중 오류가 발생했습니다.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code || !name) {
      setError('코드와 이름은 필수 항목입니다.');
      return;
    }

    mutation.mutate();
  };

  // 미저장 닫기 보호 — 기준선은 열림 시점의 값(state 초기값과 같은 식). 생성이면 빈 폼이 기준이다.
  const [initial] = useState(() => ({
    code: industry?.code || '',
    name: industry?.name || '',
    description: industry?.description || '',
    parentId: industry?.parentId || '',
    isActive: industry?.isActive ?? true,
  }));
  const isDirty =
    code !== initial.code ||
    name !== initial.name ||
    description !== initial.description ||
    parentId !== initial.parentId ||
    isActive !== initial.isActive;

  return (
    // 부모가 열 때만 마운트하므로 open은 늘 true다. 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 묶는다.
    <Modal
      open
      onClose={onClose}
      title={isEdit ? '업종 수정' : '업종 추가'}
      size="md"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          <Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </>
      }
    >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              업종 코드 *
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="예: F, F41, C10"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <p className="mt-1 text-xs text-gray-500">KSIC 코드 형식을 따릅니다.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              업종명 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 건설업, 종합 건설업"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="업종에 대한 설명 (선택사항)"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              상위 업종
            </label>
            <select
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              disabled={parentOptionsLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
            >
              <option value="">{parentOptionsLoading ? '불러오는 중…' : '없음 (최상위)'}</option>
              {parentSelectOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">
              활성화
            </label>
          </div>
        </form>
    </Modal>
  );
}

// ============================================
// 업종 상세 모달 (위험유형 매핑 관리 포함)
// ============================================

interface HazardMapping {
  id: string;
  hazardTypeId: string;
  hazardType: { id: string; code: string; name: string };
  priority: number;
  percentage: number | null;
  source: string | null;
}

function IndustryDetailModal({
  industryId,
  onClose,
}: {
  industryId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [editMapping, setEditMapping] = useState<HazardMapping | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'industries', industryId],
    queryFn: () => adminApi.getIndustry(industryId),
  });

  const industry = data?.data as IndustryDetail | undefined;
  const sortedMappings = [...(industry?.hazardMappings || [])].sort(
    (a, b) => a.priority - b.priority
  );

  // 매핑 삭제
  const deleteMappingMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteIndustryHazardMapping(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'industries', industryId] });
    },
    onError: (err) => showToast(apiErrorMessage(err, '매핑 삭제에 실패했습니다.'), 'error'),
  });

  // 우선순위 재정렬
  const reorderMutation = useMutation({
    mutationFn: (mappingIds: string[]) =>
      adminApi.reorderIndustryHazardMappings(industryId, mappingIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'industries', industryId] });
    },
    onError: (err) => showToast(apiErrorMessage(err, '순서 변경에 실패했습니다.'), 'error'),
  });

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const newOrder = sortedMappings.map((m) => m.id);
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    reorderMutation.mutate(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index >= sortedMappings.length - 1) return;
    const newOrder = sortedMappings.map((m) => m.id);
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    reorderMutation.mutate(newOrder);
  };

  // 업종 상세(z-[70]) 안에서 여므로 확인은 한 층 위(`SUB_CONFIRM_LAYER`).
  const handleDeleteMapping = async (id: string, name: string) => {
    if (
      await confirm({
        title: '위험유형 연결 삭제',
        body: `"${name}" 위험유형 연결을 삭제하시겠습니까?`,
        confirmLabel: '삭제',
        danger: true,
        ...SUB_CONFIRM_LAYER,
      })
    ) {
      deleteMappingMutation.mutate(id);
    }
  };

  return (
    // 종전 패널 스크롤(max-h-[85vh])은 base 본문 스크롤이 맡는다 — 헤더·닫기 줄이 고정된다.
    // 매핑 모달은 종전엔 이 패널의 오버레이 안에 들어 있었는데, base에서는 형제로 둔다(서브 모달 z-[80]).
    <>
    <Modal
      open
      onClose={onClose}
      title="업종 상세 정보"
      size="xl"
      panelClassName="!max-w-3xl"
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          닫기
        </button>
      }
    >
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">로딩 중...</div>
        ) : industry ? (
          <div className="space-y-6">
            {/* 기본 정보 */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">기본 정보</h4>
              <div className="bg-gray-50 rounded p-4 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">코드</div>
                  <div className="font-mono">{industry.code}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">업종명</div>
                  <div>{industry.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">레벨</div>
                  <div>L{industry.level}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">상태</div>
                  <div>
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        industry.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {industry.isActive ? '활성' : '비활성'}
                    </span>
                  </div>
                </div>
                {industry.description && (
                  <div className="col-span-2">
                    <div className="text-sm text-gray-500">설명</div>
                    <div>{industry.description}</div>
                  </div>
                )}
                {industry.parent && (
                  <div className="col-span-2">
                    <div className="text-sm text-gray-500">상위 업종</div>
                    <div>
                      <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded mr-2">
                        {industry.parent.code}
                      </span>
                      {industry.parent.name}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 하위 업종 */}
            {industry.children.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  하위 업종 ({industry.children.length}개)
                </h4>
                <div className="bg-gray-50 rounded p-4">
                  <ul className="space-y-2">
                    {industry.children.map((child) => (
                      <li key={child.id} className="flex items-center gap-2">
                        <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">
                          {child.code}
                        </span>
                        <span className={!child.isActive ? 'text-gray-400' : ''}>
                          {child.name}
                        </span>
                        {!child.isActive && (
                          <span className="text-xs text-gray-400">(비활성)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* 위험유형 매핑 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">
                  연결된 위험유형 ({sortedMappings.length}개)
                </h4>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setEditMapping(null);
                    setShowMappingModal(true);
                  }}
                >
                  <span>+</span>
                  <span>위험유형 연결</span>
                </Button>
              </div>

              {sortedMappings.length === 0 ? (
                <div className="bg-gray-50 rounded p-8 text-center text-gray-500">
                  연결된 위험유형이 없습니다.
                  <br />
                  <span className="text-sm">위 버튼을 클릭하여 위험유형을 연결하세요.</span>
                </div>
              ) : (
                <div className="bg-gray-50 rounded overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="w-20 px-3 py-2 text-center text-xs font-medium text-gray-500">
                          순서
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          코드
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          위험유형
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                          비중
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          출처
                        </th>
                        <th className="w-32 px-3 py-2 text-center text-xs font-medium text-gray-500">
                          작업
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortedMappings.map((mapping, index) => (
                        <tr key={mapping.id} className="hover:bg-gray-100">
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleMoveUp(index)}
                                disabled={index === 0 || reorderMutation.isPending}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="위로 이동"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <span className="text-sm font-medium w-5 text-center">{index + 1}</span>
                              <button
                                onClick={() => handleMoveDown(index)}
                                disabled={index === sortedMappings.length - 1 || reorderMutation.isPending}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="아래로 이동"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">
                              {mapping.hazardType?.code ?? '-'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm">{mapping.hazardType?.name ?? '-'}</td>
                          <td className="px-3 py-2 text-sm text-right">
                            {mapping.percentage != null ? (
                              <span className="bg-brand-50 text-brand-700 px-2 py-0.5 rounded">
                                {mapping.percentage}%
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-500">
                            {mapping.source || '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => {
                                  setEditMapping(mapping);
                                  setShowMappingModal(true);
                                }}
                                className="p-1 text-gray-400 hover:text-brand-600"
                                title="수정"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteMapping(mapping.id, mapping.hazardType?.name ?? '위험유형')}
                                disabled={deleteMappingMutation.isPending}
                                className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                                title="삭제"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 메타 정보 */}
            <div className="text-sm text-gray-400 pt-4 border-t">
              <p>생성: {new Date(industry.createdAt).toLocaleString('ko-KR')}</p>
              <p>수정: {new Date(industry.updatedAt).toLocaleString('ko-KR')}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">업종 정보를 찾을 수 없습니다.</div>
        )}
    </Modal>

      {confirmDialog}

      {/* 위험유형 매핑 모달 */}
      {showMappingModal && (
        <HazardMappingModal
          industryId={industryId}
          existingMappings={sortedMappings}
          editMapping={editMapping}
          onClose={() => {
            setShowMappingModal(false);
            setEditMapping(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'industries', industryId] });
            setShowMappingModal(false);
            setEditMapping(null);
          }}
        />
      )}
    </>
  );
}

// ============================================
// 위험유형 매핑 추가/수정 모달
// ============================================

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function HazardMappingModal({
  industryId,
  existingMappings,
  editMapping,
  onClose,
  onSuccess,
}: {
  industryId: string;
  existingMappings: HazardMapping[];
  editMapping: HazardMapping | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [hazardTypeId, setHazardTypeId] = useState(editMapping?.hazardTypeId || '');
  const [percentage, setPercentage] = useState(
    editMapping?.percentage != null ? String(editMapping.percentage) : ''
  );
  const [source, setSource] = useState(editMapping?.source || '');
  const formId = useId();

  const isEdit = !!editMapping;

  // 위험유형 목록 조회 (대분류만)
  const { data: hazardTypesData, isLoading: hazardTypesLoading } = useQuery({
    queryKey: ['admin', 'hazard-types', 'level1'],
    queryFn: () => adminApi.getHazardTypes({ level: 1, isActive: true }),
  });

  const hazardTypes = hazardTypesData?.data || [];

  // 이미 연결된 위험유형 ID 목록 (수정 시 자기 자신 제외)
  const existingHazardTypeIds = new Set(
    existingMappings
      .filter((m) => m.id !== editMapping?.id)
      .map((m) => m.hazardTypeId)
  );

  // 선택 가능한 위험유형 (이미 연결된 것 제외)
  const availableHazardTypes = hazardTypes.filter(
    (h) => !existingHazardTypeIds.has(h.id)
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const percentageNum = percentage ? parseFloat(percentage) : undefined;
      if (isEdit) {
        return await adminApi.updateIndustryHazardMapping(editMapping.id, {
          percentage: percentageNum,
          source: source || undefined,
        });
      } else {
        return await adminApi.createIndustryHazardMapping({
          industryId,
          hazardTypeId,
          percentage: percentageNum,
          source: source || undefined,
        });
      }
    },
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEdit && !hazardTypeId) {
      alert('위험유형을 선택해주세요.');
      return;
    }
    mutation.mutate();
  };

  // 미저장 닫기 보호 — 기준선은 열림 시점의 값(state 초기값과 같은 식). 확인 창은 base가 z-[90]에 띄운다.
  const [initial] = useState(() => ({
    hazardTypeId: editMapping?.hazardTypeId || '',
    percentage: editMapping?.percentage != null ? String(editMapping.percentage) : '',
    source: editMapping?.source || '',
  }));
  const isDirty =
    hazardTypeId !== initial.hazardTypeId ||
    percentage !== initial.percentage ||
    source !== initial.source;

  return (
    // 업종 상세 위에 겹쳐 뜨는 서브 모달이라 한 층 위(z-[80]). 제출 버튼은 `form` 속성으로 폼에 묶는다.
    <Modal
      open
      onClose={onClose}
      title={isEdit ? '위험유형 연결 수정' : '위험유형 연결 추가'}
      size="md"
      overlayZClassName="z-[80]"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={mutation.isPending || (!isEdit && !hazardTypeId)}
          >
            {mutation.isPending ? '저장 중...' : isEdit ? '수정' : '추가'}
          </Button>
        </>
      }
    >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {/* 위험유형 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              위험유형 <span className="text-red-500">*</span>
            </label>
            {isEdit ? (
              <div className="px-3 py-2 bg-gray-100 rounded">
                <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded mr-2">
                  {editMapping.hazardType.code}
                </span>
                {editMapping.hazardType.name}
              </div>
            ) : hazardTypesLoading ? (
              <div className="px-3 py-2 text-gray-500">로딩 중...</div>
            ) : availableHazardTypes.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 bg-gray-50 rounded">
                연결 가능한 위험유형이 없습니다.
              </div>
            ) : (
              <select
                value={hazardTypeId}
                onChange={(e) => setHazardTypeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
                required
              >
                <option value="">선택하세요</option>
                {availableHazardTypes.map((h) => (
                  <option key={h.id} value={h.id}>
                    [{h.code}] {h.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 비중 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              비중 (%)
            </label>
            <input
              type="number"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              placeholder="예: 33.6"
              min="0"
              max="100"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              산업재해 통계 기반 비중 (선택 사항)
            </p>
          </div>

          {/* 출처 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              출처
            </label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="예: 2024 고용노동부"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </form>
    </Modal>
  );
}
